import React, { useEffect, useState } from 'react';
import { HolidayResponse, Holiday } from '../../interfaces/holiday.interface';
import { HolidayService } from '../../services/holiday.service';
import { Box, Button, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { format } from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import { UserType } from '../../interfaces/userType.enum';
import { useToast } from '../../context/ToastContext';

const HolidayList: React.FC = () => {
    const [holidayData, setHolidayData] = useState<HolidayResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const { user } = useAuth();
    const toast = useToast();
    const { t } = useTranslation(['holiday', 'common']);

    // Check if user is an institution admin
    const isInstitutionAdmin = user?.roles.includes(UserType.InstitutionAdmin);

    useEffect(() => {
        fetchHolidays();
    }, []);

    const fetchHolidays = async () => {
        try {
            const data = await HolidayService.getHolidaysByInstitution();
            setHolidayData(data);
        } catch (err) {
            toast.showApiError(err, t('holidayList.fetchFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (window.confirm(t('holidayList.confirmDelete'))) {
            try {
                await HolidayService.deleteHoliday(id);
                await fetchHolidays();
            } catch (err) {
                toast.showApiError(err, t('holidayList.deleteFailed'));
            }
        }
    };

    const handleEdit = (holiday: Holiday) => {
        navigate(`/holidays/edit/${holiday.id}`, { state: { holiday } });
    };

    if (loading) return <Typography>{t('holidayList.loading')}</Typography>;
    if (!holidayData) return <Typography>{t('holidayList.empty')}</Typography>;

    return (
        <Box sx={{ padding: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">{t('holidayList.title')}</Typography>
                {isInstitutionAdmin && (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => navigate('/holidays/create')}
                    >
                        {t('holidayList.add')}
                    </Button>
                )}
            </Box>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('common:fields.name')}</TableCell>
                            <TableCell>{t('fields.startDate')}</TableCell>
                            <TableCell>{t('fields.endDate')}</TableCell>
                            <TableCell>{t('common:fields.description')}</TableCell>
                            {isInstitutionAdmin && <TableCell>{t('common:fields.actions')}</TableCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {holidayData.content.map((holiday) => (
                            <TableRow key={holiday.id}>
                                <TableCell>{holiday.name}</TableCell>
                                <TableCell>{format(new Date(holiday.startDate), 'dd-MM-yyyy')}</TableCell>
                                <TableCell>{format(new Date(holiday.endDate), 'dd-MM-yyyy')}</TableCell>
                                <TableCell>{holiday.description}</TableCell>
                                {isInstitutionAdmin && (
                                    <TableCell>
                                        <IconButton onClick={() => handleEdit(holiday)} color="primary" aria-label={t('holidayList.editAria')}>
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton onClick={() => handleDelete(holiday.id)} color="error" aria-label={t('holidayList.deleteAria')}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default HolidayList;
